import { yupResolver } from '@hookform/resolvers/yup';
import cx from 'classnames';
import Alert from 'components/Alert/Alert';
import Button from 'components/Button';
import Modal from 'components/Modal';
import StepIndicator from 'components/StepIndicator';
import T from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { createStudents, setOperatingClassroom } from 'store/actions/classroom';
import { createClassroom, getUser } from 'store/actions/user';
import * as yup from 'yup';

import CreateClass from './components/CreateClass/CreateClass';
import CreateStudents from './components/CreateStudents/CreateStudents';
import ShareClassCode from './components/ShareClassCode/ShareClassCode';
import ShareLogin from './components/ShareLogin/ShareLogin';
import Welcome from './components/Welcome/Welcome';
import styles from './CreateNewClassForm.module.scss';
import { getCookie } from 'utils/cookie';

const createClassSchema = yup.object().shape({
  className: yup.string().required('Class name is a required filed').min(3, 'Class name must be minimum 3 characters.'),
});

const createStudentsSchema = yup.object().shape({
  students_number: yup.string().required(),
  password: yup
    .string()
    .required('New password field is required.')
    .min(8, 'Password must be at least 8 characters.')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*(\d|\W)).{8,}$/,
      'Passwords must include an upper and lowercase letter, and at least one number or special character.',
    ),
});

const defaultSchemas = { 1: createClassSchema, 2: createStudentsSchema };
const oidcSchemas = { 1: createClassSchema };

const ConditionalWrapper = ({ condition, wrapper, children }) => (condition ? wrapper(children) : children);

const CreateNewClassForm = ({
  modal = true,
  welcomeStep = true,
  classroomStep = true,
  handleCancel,
  masterPassword,
}) => {
  // const { classId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    title: '',
  });

  const { id: userId, first_name, username, classrooms = [], isLoading, oidc_user } = useSelector(({ user }) => user);

  const {
    operatingClassroom: { id: classroomId },
  } = useSelector(({ classroom }) => classroom);

  const schemas = oidc_user ? oidcSchemas : defaultSchemas;
  const formMethods = useForm({
    resolver: yupResolver(schemas[activeStep]),
    mode: 'onChange',
  });
  const printComponentRef = useRef();
  const modal_state = useSelector((state) => state.modalState);

  const {
    setValue,
    getValues,
    formState: { isValid },
  } = formMethods;
  const isWelcomeStep = activeStep === 0;
  const lastStepNumber = oidc_user ? 2 : 3;
  const isLastStep = activeStep === lastStepNumber;

  useEffect(() => {
    if (masterPassword) {
      setValue('password', masterPassword);
    }
  }, [masterPassword, setValue]);

  useEffect(() => {
    if (!welcomeStep && !classroomStep) {
      setActiveStep(2);
    } else if (!welcomeStep) {
      setActiveStep(1);
    } else {
      setActiveStep(0);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (modal_state.modalState !== 'open' && username && !isLoading && classrooms.length === 0) {
        setModalOpen(true);
        setActiveStep(0);
      } else {
        setModalOpen(false);
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [username, modal_state, first_name]);

  const handlePrint = useReactToPrint({
    content: () => printComponentRef.current,
  });

  const handleCreateClassroom = useCallback(async () => {
    const { className } = getValues();

    dispatch(
      createClassroom(className, (classroom) => {
        dispatch(setOperatingClassroom(classroom));
        setActiveStep(activeStep + 1);
        setAlert({
          open: true,
          title: `${classroom?.name} has been created.`,
        });
      }),
    );
  }, [dispatch, getValues, activeStep]);

  const handleCreateStudentsAccounts = useCallback(async () => {
    const { students_number, password } = getValues();

    const normalizedData = {
      user_key: getCookie('user_token'),
      classroom_id: classroomId,
      students_number: Number(students_number),
      password,
    };

    dispatch(
      createStudents(normalizedData, (accounts) => {
        setActiveStep(activeStep + 1);
        setAlert({
          open: true,
          title: `${accounts?.students.length} accounts have been created!`,
        });
      }),
    );
  }, [activeStep, classroomId, dispatch, getValues]);

  const handleNext = useCallback(() => {
    if (oidc_user) {
      switch (activeStep) {
        case 1:
          return handleCreateClassroom();
        case 2:
          return handlePrint();
        default:
          setActiveStep(activeStep + 1);
      }
    } else {
      switch (activeStep) {
        case 1:
          return handleCreateClassroom();
        case 2:
          setIsLoadingStudents(true);
          return handleCreateStudentsAccounts();
        case 3:
          return handlePrint();
        default:
          setActiveStep(activeStep + 1);
      }
    }
  }, [activeStep, oidc_user, handleCreateClassroom, handleCreateStudentsAccounts, handlePrint]);

  const handleCloseAlert = useCallback(() => {
    setAlert(false);
  }, []);

  const handleCloseModal = useCallback(() => {
    dispatch(getUser());
    if (handleCancel) {
      return handleCancel();
    }

    setModalOpen(false);
    navigate(-1);
  }, [handleCancel, navigate]);

  const steps = useMemo(
    () => oidc_user
      ? [
          {
            title: 'Welcome',
            component: <Welcome first_name={first_name} username={username} />,
          },
          {
            title: 'Create Class',
            component: <CreateClass />,
          },
          {
            title: 'Share Classroom Code',
            component: <ShareClassCode componentRef={printComponentRef} />,
          },
        ]
      : [
          {
            title: 'Welcome',
            component: <Welcome first_name={first_name} username={username} />,
          },
          {
            title: 'Create Class',
            component: <CreateClass />,
          },
          {
            title: 'Create Students',
            component: <CreateStudents masterPassword={masterPassword} />,
          },
          {
            title: 'Share Login Credentials',
            component: <ShareLogin componentRef={printComponentRef} />,
          },
        ],
    [oidc_user, masterPassword, username, first_name],
  );

  if (!username || activeStep === null) {
    return null;
  }

  return (
    <>
      <ConditionalWrapper
        condition={modal}
        wrapper={(children) => (
          <Modal className={styles.modalBody} isOpen={modalOpen}>
            {children}
          </Modal>
        )}
      >
        <>
          {!!activeStep && (
            <div className={cx(classroomStep ? styles.stepsWrapper : styles.stepsWrapperSigle)}>
              {classroomStep && <StepIndicator current={activeStep} steps={oidc_user ? 2 : 3} />}

              <Alert open={alert.open} title={alert.title} type="success" handleClose={handleCloseAlert} />
            </div>
          )}

          <FormProvider {...formMethods}>
            <form
              className={cx(styles.formWrapper, {
                [styles.firstStep]: isWelcomeStep,
                [styles.lastStep]: isLastStep,
              })}
            >
              {steps[activeStep].component}

              {isWelcomeStep ? (
                <div className={styles.buttonWrapper}>
                  <Button className="mgt-35" label="GET STARTED" variant="outlined" onClick={handleNext} />
                </div>
              ) : (
                <div className={styles.buttonStack}>
                  <Button
                    label={isLastStep ? 'Close' : 'Cancel'}
                    className={styles.cancelButton}
                    onClick={handleCloseModal}
                  />

                  {!(isLastStep && oidc_user) && (
                    <Button
                      label={isLastStep ? 'Print Usernames' : 'Next'}
                      onClick={handleNext}
                      disabled={(isLoadingStudents && !isLastStep) || !isValid}
                    />
                  )}
                </div>
              )}
            </form>
          </FormProvider>
        </>
      </ConditionalWrapper>
    </>
  );
};

CreateNewClassForm.propTypes = {
  modal: T.bool,
  welcomeStep: T.bool,
};
export default CreateNewClassForm;
